import { useCallback, useEffect, useRef } from 'react';

/**
 * useIdleTimeout
 *
 * Tracks user activity and fires:
 *   - onWarn   when (idleMs - warningMs) of inactivity has elapsed
 *   - onExpire when idleMs of inactivity has elapsed
 *
 * Any subsequent user activity (or a manual reset() call) cancels pending
 * timers and re-arms them. Activity is also synchronized across tabs via
 * BroadcastChannel, so interacting with one tab keeps siblings alive.
 */
export interface UseIdleTimeoutOptions {
  /** Total inactivity before onExpire fires (ms). */
  idleMs: number;
  /** How long before onExpire to fire onWarn (ms). Must be < idleMs. */
  warningMs: number;
  /** Called when warning threshold is reached. */
  onWarn: () => void;
  /** Called when full idle threshold is reached. */
  onExpire: () => void;
  /** Optional: invoked on every detected activity event (throttled by browser). */
  onActivity?: () => void;
  /** When false, no timers are armed. Default true. */
  enabled?: boolean;
}

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'click',
  'touchstart',
  'scroll',
];

const CHANNEL_NAME = 'apptesting-activity';

export function useIdleTimeout({
  idleMs,
  warningMs,
  onWarn,
  onExpire,
  onActivity,
  enabled = true,
}: UseIdleTimeoutOptions): { reset: () => void } {
  const warnTimerRef = useRef<number | null>(null);
  const expireTimerRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Keep latest callbacks in refs so the effect below doesn't reattach
  // listeners every render.
  const onWarnRef = useRef(onWarn);
  const onExpireRef = useRef(onExpire);
  const onActivityRef = useRef(onActivity);
  onWarnRef.current = onWarn;
  onExpireRef.current = onExpire;
  onActivityRef.current = onActivity;

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current !== null) {
      window.clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (expireTimerRef.current !== null) {
      window.clearTimeout(expireTimerRef.current);
      expireTimerRef.current = null;
    }
  }, []);

  const armTimers = useCallback(() => {
    clearTimers();
    const warnDelay = Math.max(0, idleMs - warningMs);
    warnTimerRef.current = window.setTimeout(() => {
      try {
        onWarnRef.current();
      } catch (e) {
        console.error('useIdleTimeout onWarn error:', e);
      }
    }, warnDelay);
    expireTimerRef.current = window.setTimeout(() => {
      try {
        onExpireRef.current();
      } catch (e) {
        console.error('useIdleTimeout onExpire error:', e);
      }
    }, idleMs);
  }, [idleMs, warningMs, clearTimers]);

  const reset = useCallback(() => {
    if (!enabled) return;
    armTimers();
    // Broadcast so other tabs also reset.
    try {
      channelRef.current?.postMessage({ type: 'activity', at: Date.now() });
    } catch {
      /* ignore */
    }
  }, [enabled, armTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Cross-tab activity sync.
    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        channel = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = channel;
        channel.onmessage = (ev) => {
          if (ev?.data?.type === 'activity') {
            armTimers();
          }
        };
      } catch {
        channel = null;
        channelRef.current = null;
      }
    }

    const handleActivity = () => {
      try {
        onActivityRef.current?.();
      } catch {
        /* ignore */
      }
      armTimers();
      try {
        channelRef.current?.postMessage({ type: 'activity', at: Date.now() });
      } catch {
        /* ignore */
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Treat tab refocus as activity so the warning doesn't pop instantly
        // when the user returns.
        handleActivity();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibility);

    armTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      if (channel) {
        try {
          channel.close();
        } catch {
          /* ignore */
        }
      }
      channelRef.current = null;
      clearTimers();
    };
  }, [enabled, armTimers, clearTimers]);

  return { reset };
}
